import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  AlignJustify,
  ArrowUpDown,
  Building2,
  Calendar,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightSmall,
  Download,
  Eye,
  Hammer,
  Layers,
  Loader2,
  Menu as MenuIcon,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Rows3,
  Search,
  Table2,
  Trash2,
  Upload,
  Wrench,
  X,
  XCircle,
} from 'lucide-react';
import type { BatimentStatut, EtageStatut, SalleStatut, SalleType } from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { BatimentFormModal } from '../components/batiments/BatimentFormModal';
import { EtageFormModal } from '../components/batiments/EtageFormModal';
import { SalleFormModal } from '../components/batiments/SalleFormModal';
import { BatimentDetailDrawer } from '../components/batiments/BatimentDetailDrawer';
import { SalleDetailDrawer } from '../components/batiments/SalleDetailDrawer';
import { ImportBatimentsModal } from '../components/batiments/ImportBatimentsModal';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Page `/batiments` — vue tableau unifié hiérarchique (US-BAT-01 à 07).
 *
 * Inspiré du mockup `variant-unified-table.html` : table avec groupes
 * pliants (bâtiment → étage → salle), sélection multiple, édition inline
 * possible. Stats en KPIs en tête. Boutons import/export/nouveau.
 */

interface BatimentNode {
  id: string;
  code: string;
  nom: string;
  type: string;
  statut: BatimentStatut;
  surface_totale: number | null;
  nombre_etages: number;
  etages: EtageNode[];
}

interface EtageNode {
  id: string;
  code: string;
  numero: string;
  nom: string | null;
  statut: EtageStatut;
  surface_totale: number | null;
  salles: SalleNode[];
}

interface SalleNode {
  id: string;
  code: string;
  numero_porte: string | null;
  nom: string;
  type: SalleType;
  statut: SalleStatut;
  surface: number | null;
  service_id: string | null;
  service: { id: string; nom_complet: string } | null;
  est_reservable: boolean;
}

interface Stats {
  total: number;
  surface_totale: number;
  en_service: number;
  en_travaux: number;
  ferme_temporairement: number;
  en_projet: number;
  desaffecte: number;
  by_type: Array<{ type: string; count: number }>;
}

type CreateMode =
  | null
  | { type: 'batiment' }
  | { type: 'etage'; batimentId: string }
  | { type: 'salle'; etageId: string };

type Density = 'compact' | 'standard' | 'confort';

interface InlineEdit {
  salleId: string;
  field: 'nom' | 'statut';
  value: string;
}

/**
 * Padding vertical des lignes selon la densité (US-BAT-04 mockup). Le reste
 * de la mise en page (icônes, taille de police) reste constant pour ne pas
 * casser l'alignement. La densité est persistée dans `localStorage` pour
 * survivre aux rechargements.
 */
const DENSITY_PADDING: Record<Density, { salle: string; etage: string; batiment: string }> = {
  compact: { salle: 'py-1', etage: 'py-1', batiment: 'py-1.5' },
  standard: { salle: 'py-2', etage: 'py-1.5', batiment: 'py-2' },
  confort: { salle: 'py-3', etage: 'py-2.5', batiment: 'py-3' },
};

const DENSITY_STORAGE_KEY = 'mygmao.batiments.density';

export function BatimentsPage() {
  const [hierarchy, setHierarchy] = useState<BatimentNode[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState<string>('');
  const [batimentFilter, setBatimentFilter] = useState<string>('');

  const [expandedBatiments, setExpandedBatiments] = useState<Set<string>>(new Set());
  const [expandedEtages, setExpandedEtages] = useState<Set<string>>(new Set());
  const [selectedSalles, setSelectedSalles] = useState<Set<string>>(new Set());

  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === 'undefined') return 'standard';
    const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return stored === 'compact' || stored === 'confort' ? stored : 'standard';
  });
  useEffect(() => {
    window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
  }, [density]);
  const pad = DENSITY_PADDING[density];

  const [createMode, setCreateMode] = useState<CreateMode>(null);
  const [editingBatiment, setEditingBatiment] = useState<string | null>(null);
  const [editingEtage, setEditingEtage] = useState<string | null>(null);
  const [editingSalle, setEditingSalle] = useState<string | null>(null);
  const [detailBatiment, setDetailBatiment] = useState<string | null>(null);
  const [detailSalle, setDetailSalle] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [inlineEdit, setInlineEdit] = useState<InlineEdit | null>(null);
  const [savingInline, setSavingInline] = useState(false);

  async function commitInlineEdit() {
    if (!inlineEdit) return;
    setSavingInline(true);
    try {
      await api.patch(`/salles/${inlineEdit.salleId}`, {
        [inlineEdit.field]: inlineEdit.value,
      });
      setInlineEdit(null);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSavingInline(false);
    }
  }

  function cancelInlineEdit() {
    setInlineEdit(null);
  }

  const { can } = usePermissions();
  const canImport = can('batiments', 'update');

  async function handleExport() {
    setExporting(true);
    setErrorMessage(null);
    try {
      const res = await api.get('/batiments/export.xlsx', { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batiments-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    refresh();
    loadStats();
  }, []);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: BatimentNode[] }>('/batiments/hierarchy');
      setHierarchy(res.data.data);
      // Expand le 1er bâtiment + son 1er étage par défaut (cohérent mockup).
      if (res.data.data.length > 0 && expandedBatiments.size === 0) {
        const first = res.data.data[0]!;
        setExpandedBatiments(new Set([first.id]));
        if (first.etages.length > 0) {
          setExpandedEtages(new Set([first.etages[0]!.id]));
        }
      }
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await api.get<{ data: Stats }>('/batiments/stats');
      setStats(res.data.data);
    } catch {
      // Silencieux.
    }
  }

  function toggleBatiment(id: string) {
    setExpandedBatiments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleEtage(id: string) {
    setExpandedEtages((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSalleSelection(id: string) {
    setSelectedSalles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedSalles(new Set());
  }

  async function handleBulkChangeStatut(newStatut: SalleStatut) {
    if (selectedSalles.size === 0) return;
    if (!confirm(`Changer le statut de ${selectedSalles.size} salle(s) vers "${newStatut}" ?`))
      return;
    try {
      await Promise.all(
        Array.from(selectedSalles).map((id) => api.patch(`/salles/${id}`, { statut: newStatut })),
      );
      clearSelection();
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleBulkDelete() {
    if (selectedSalles.size === 0) return;
    if (!confirm(`Supprimer définitivement ${selectedSalles.size} salle(s) ?`)) return;
    try {
      await Promise.all(Array.from(selectedSalles).map((id) => api.delete(`/salles/${id}`)));
      clearSelection();
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  // Filtrage côté client (la hiérarchie est paginée côté API à 500 bâtiments max).
  const filteredHierarchy = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hierarchy
      .filter((b) => !batimentFilter || b.id === batimentFilter)
      .filter((b) => !statutFilter || b.statut === statutFilter)
      .map((b) => ({
        ...b,
        etages: b.etages.map((e) => ({
          ...e,
          salles: e.salles.filter((s) => {
            if (!q) return true;
            return (
              s.code.toLowerCase().includes(q) ||
              s.nom.toLowerCase().includes(q) ||
              (s.numero_porte ?? '').toLowerCase().includes(q)
            );
          }),
        })),
      }));
  }, [hierarchy, search, statutFilter, batimentFilter]);

  const totalSalles = filteredHierarchy.reduce(
    (sum, b) => sum + b.etages.reduce((s, e) => s + e.salles.length, 0),
    0,
  );

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Table2 className="w-7 h-7 mr-2 text-primary-600" />
            Gestion des espaces
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalSalles} salle(s) · groupées par bâtiment / étage
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {canImport && (
            <button
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importer
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter
          </button>
          <Link
            to="/batiments/reservations"
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Réservations
          </Link>
          <button
            onClick={() => setCreateMode({ type: 'batiment' })}
            className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau bâtiment
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Total bâtiments" value={stats.total} tone="default" />
          <Kpi
            label="Surface totale"
            value={`${Math.round(stats.surface_totale)} m²`}
            tone="primary"
          />
          <Kpi label="En service" value={stats.en_service} tone="green" />
          <Kpi label="En travaux" value={stats.en_travaux} tone="yellow" />
          <Kpi
            label="Désaffectés / fermés"
            value={stats.desaffecte + stats.ferme_temporairement}
            tone="gray"
          />
        </div>
      )}

      {/* Contrôles */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-3 text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <CheckSquare className="w-4 h-4 text-gray-400" />
            <span>Vue hiérarchique bâtiment → étage → salle</span>
          </div>
          <span className="text-gray-300">|</span>
          <div className="flex items-center space-x-1" role="radiogroup" aria-label="Densité">
            <span className="text-xs font-medium text-gray-500">Densité :</span>
            <button
              onClick={() => setDensity('compact')}
              role="radio"
              aria-checked={density === 'compact'}
              title="Compact"
              className={`p-1.5 rounded ${
                density === 'compact'
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <AlignJustify className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setDensity('standard')}
              role="radio"
              aria-checked={density === 'standard'}
              title="Standard"
              className={`p-1.5 rounded ${
                density === 'standard'
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <MenuIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setDensity('confort')}
              role="radio"
              aria-checked={density === 'confort'}
              title="Confort"
              className={`p-1.5 rounded ${
                density === 'confort'
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <Rows3 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={batimentFilter}
            onChange={(e) => setBatimentFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les bâtiments</option>
            {hierarchy.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nom}
              </option>
            ))}
          </select>
          <select
            value={statutFilter}
            onChange={(e) => setStatutFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tous les statuts</option>
            <option value="en_service">En service</option>
            <option value="en_travaux">En travaux</option>
            <option value="ferme_temporairement">Fermé temporairement</option>
            <option value="en_projet">En projet</option>
            <option value="desaffecte">Désaffecté</option>
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une salle…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={() => {
              refresh();
              loadStats();
            }}
            className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedSalles.size > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-primary-900">
              <CheckSquare className="inline w-4 h-4 mr-1" />
              {selectedSalles.size} salle(s) sélectionnée(s)
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleBulkChangeStatut(e.target.value as SalleStatut);
                  e.target.value = '';
                }
              }}
              className="px-3 py-1.5 text-xs font-medium border border-primary-300 bg-white rounded-lg text-primary-700 hover:bg-primary-100"
              defaultValue=""
            >
              <option value="">Changer statut…</option>
              <option value="disponible">Disponible</option>
              <option value="occupee">Occupée</option>
              <option value="en_maintenance">En maintenance</option>
              <option value="en_nettoyage">En nettoyage</option>
              <option value="hors_service">Hors service</option>
              <option value="en_travaux">En travaux</option>
            </select>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-red-300 bg-white rounded-lg text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Supprimer
            </button>
            <button
              onClick={clearSelection}
              className="text-gray-400 hover:text-gray-600 ml-2"
              aria-label="Désélectionner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-3 w-10"></th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Code</span>
                    <ArrowUpDown className="w-3 h-3 text-gray-400" />
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="hidden lg:table-cell px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Surface
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="hidden xl:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service affecté
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : filteredHierarchy.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucun bâtiment. Crée le premier pour démarrer.
                  </td>
                </tr>
              ) : (
                filteredHierarchy.map((batiment) => (
                  <BatimentRows
                    key={batiment.id}
                    batiment={batiment}
                    expanded={expandedBatiments.has(batiment.id)}
                    expandedEtages={expandedEtages}
                    selectedSalles={selectedSalles}
                    pad={pad}
                    inlineEdit={inlineEdit}
                    savingInline={savingInline}
                    onToggleBatiment={() => toggleBatiment(batiment.id)}
                    onToggleEtage={toggleEtage}
                    onToggleSalleSelection={toggleSalleSelection}
                    onEditBatiment={() => setEditingBatiment(batiment.id)}
                    onDetailBatiment={() => setDetailBatiment(batiment.id)}
                    onAddEtage={() => setCreateMode({ type: 'etage', batimentId: batiment.id })}
                    onEditEtage={(id) => setEditingEtage(id)}
                    onAddSalle={(etageId) => setCreateMode({ type: 'salle', etageId })}
                    onEditSalle={(id) => setEditingSalle(id)}
                    onDetailSalle={(id) => setDetailSalle(id)}
                    onStartInlineEdit={(salleId, field, value) =>
                      setInlineEdit({ salleId, field, value })
                    }
                    onChangeInlineEdit={(value) =>
                      setInlineEdit((prev) => (prev ? { ...prev, value } : prev))
                    }
                    onCommitInlineEdit={commitInlineEdit}
                    onCancelInlineEdit={cancelInlineEdit}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {createMode?.type === 'batiment' && (
        <BatimentFormModal
          onClose={() => setCreateMode(null)}
          onSaved={() => {
            setCreateMode(null);
            refresh();
            loadStats();
          }}
        />
      )}
      {createMode?.type === 'etage' && (
        <EtageFormModal
          batimentId={createMode.batimentId}
          onClose={() => setCreateMode(null)}
          onSaved={() => {
            setCreateMode(null);
            refresh();
          }}
        />
      )}
      {createMode?.type === 'salle' && (
        <SalleFormModal
          etageId={createMode.etageId}
          onClose={() => setCreateMode(null)}
          onSaved={() => {
            setCreateMode(null);
            refresh();
          }}
        />
      )}
      {editingBatiment && (
        <BatimentFormModal
          batimentId={editingBatiment}
          onClose={() => setEditingBatiment(null)}
          onSaved={() => {
            setEditingBatiment(null);
            refresh();
            loadStats();
          }}
        />
      )}
      {editingEtage && (
        <EtageFormModal
          etageId={editingEtage}
          onClose={() => setEditingEtage(null)}
          onSaved={() => {
            setEditingEtage(null);
            refresh();
          }}
        />
      )}
      {editingSalle && (
        <SalleFormModal
          salleId={editingSalle}
          onClose={() => setEditingSalle(null)}
          onSaved={() => {
            setEditingSalle(null);
            refresh();
          }}
        />
      )}
      {detailBatiment && (
        <BatimentDetailDrawer batimentId={detailBatiment} onClose={() => setDetailBatiment(null)} />
      )}
      {detailSalle && (
        <SalleDetailDrawer salleId={detailSalle} onClose={() => setDetailSalle(null)} />
      )}
      {importOpen && (
        <ImportBatimentsModal
          onClose={() => setImportOpen(false)}
          onCompleted={() => {
            setImportOpen(false);
            refresh();
            loadStats();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Rows
// ─────────────────────────────────────────────────────────────────────────────

interface InlineEditProps {
  inlineEdit: InlineEdit | null;
  savingInline: boolean;
  onStartInlineEdit(salleId: string, field: 'nom' | 'statut', value: string): void;
  onChangeInlineEdit(value: string): void;
  onCommitInlineEdit(): void;
  onCancelInlineEdit(): void;
}

function BatimentRows({
  batiment,
  expanded,
  expandedEtages,
  selectedSalles,
  pad,
  inlineEdit,
  savingInline,
  onToggleBatiment,
  onToggleEtage,
  onToggleSalleSelection,
  onEditBatiment,
  onDetailBatiment,
  onAddEtage,
  onEditEtage,
  onAddSalle,
  onEditSalle,
  onDetailSalle,
  onStartInlineEdit,
  onChangeInlineEdit,
  onCommitInlineEdit,
  onCancelInlineEdit,
}: {
  batiment: BatimentNode;
  expanded: boolean;
  expandedEtages: Set<string>;
  selectedSalles: Set<string>;
  pad: { salle: string; etage: string; batiment: string };
  onToggleBatiment(): void;
  onToggleEtage(id: string): void;
  onToggleSalleSelection(id: string): void;
  onEditBatiment(): void;
  onDetailBatiment(): void;
  onAddEtage(): void;
  onEditEtage(id: string): void;
  onAddSalle(etageId: string): void;
  onEditSalle(id: string): void;
  onDetailSalle(id: string): void;
} & InlineEditProps) {
  const totalSalles = batiment.etages.reduce((s, e) => s + e.salles.length, 0);
  const bgGroup = expanded ? 'bg-primary-50/60' : 'bg-primary-50/30';

  return (
    <>
      {/* Bâtiment header */}
      <tr className={`${bgGroup} border-y border-primary-100 hover:bg-primary-50`}>
        <td colSpan={7} className={`px-3 ${pad.batiment}`}>
          <button onClick={onToggleBatiment} className="flex items-center w-full text-left">
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-primary-700 mr-2 flex-shrink-0" />
            ) : (
              <ChevronRightSmall className="w-4 h-4 text-primary-700 mr-2 flex-shrink-0" />
            )}
            <Building2 className="w-4 h-4 text-primary-700 mr-2 flex-shrink-0" />
            <span className="font-semibold text-primary-900">{batiment.code}</span>
            <span className="text-primary-700 mx-2">·</span>
            <span className="text-sm text-primary-800 truncate">{batiment.nom}</span>
            <BatimentStatutBadge statut={batiment.statut} className="ml-2" />
            <span className="ml-auto text-xs text-primary-700 font-medium whitespace-nowrap">
              {batiment.etages.length} étage(s) · {totalSalles} salle(s)
            </span>
          </button>
        </td>
        <td className={`px-3 ${pad.batiment} text-right`}>
          <div className="flex items-center justify-end space-x-1">
            <button
              onClick={onDetailBatiment}
              className="p-1.5 rounded text-primary-700 hover:bg-primary-100"
              title="Détails"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={onEditBatiment}
              className="p-1.5 rounded text-primary-700 hover:bg-primary-100"
              title="Modifier"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <>
          {batiment.etages.length === 0 ? (
            <tr>
              <td colSpan={8} className="px-3 py-2 pl-12 text-xs text-gray-500 italic">
                Aucun étage rattaché.
                <button
                  onClick={onAddEtage}
                  className="ml-2 text-primary-600 hover:underline inline-flex items-center"
                >
                  <Plus className="w-3 h-3 mr-0.5" />
                  Ajouter un étage
                </button>
              </td>
            </tr>
          ) : (
            batiment.etages.map((etage) => (
              <EtageRows
                key={etage.id}
                etage={etage}
                expanded={expandedEtages.has(etage.id)}
                selectedSalles={selectedSalles}
                pad={pad}
                inlineEdit={inlineEdit}
                savingInline={savingInline}
                onStartInlineEdit={onStartInlineEdit}
                onChangeInlineEdit={onChangeInlineEdit}
                onCommitInlineEdit={onCommitInlineEdit}
                onCancelInlineEdit={onCancelInlineEdit}
                onToggle={() => onToggleEtage(etage.id)}
                onToggleSalleSelection={onToggleSalleSelection}
                onEditEtage={() => onEditEtage(etage.id)}
                onAddSalle={() => onAddSalle(etage.id)}
                onEditSalle={onEditSalle}
                onDetailSalle={onDetailSalle}
              />
            ))
          )}
          <tr>
            <td colSpan={8} className="px-3 py-1.5 pl-12 bg-gray-50/50">
              <button
                onClick={onAddEtage}
                className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center"
              >
                <Plus className="w-3 h-3 mr-1" />
                Ajouter un étage à ce bâtiment
              </button>
            </td>
          </tr>
        </>
      )}
    </>
  );
}

function EtageRows({
  etage,
  expanded,
  selectedSalles,
  pad,
  inlineEdit,
  savingInline,
  onToggle,
  onToggleSalleSelection,
  onEditEtage,
  onAddSalle,
  onEditSalle,
  onDetailSalle,
  onStartInlineEdit,
  onChangeInlineEdit,
  onCommitInlineEdit,
  onCancelInlineEdit,
}: {
  etage: EtageNode;
  expanded: boolean;
  selectedSalles: Set<string>;
  pad: { salle: string; etage: string; batiment: string };
  onToggle(): void;
  onToggleSalleSelection(id: string): void;
  onEditEtage(): void;
  onAddSalle(): void;
  onEditSalle(id: string): void;
  onDetailSalle(id: string): void;
} & InlineEditProps) {
  return (
    <>
      <tr className="bg-blue-50/40 hover:bg-blue-50">
        <td colSpan={7} className={`px-3 ${pad.etage} pl-8`}>
          <button onClick={onToggle} className="flex items-center w-full text-left">
            {expanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-blue-700 mr-2 flex-shrink-0" />
            ) : (
              <ChevronRightSmall className="w-3.5 h-3.5 text-blue-700 mr-2 flex-shrink-0" />
            )}
            <Layers className="w-3.5 h-3.5 text-blue-600 mr-2 flex-shrink-0" />
            <span className="text-xs font-semibold text-blue-900">
              Étage {etage.numero}
              {etage.nom && ` — ${etage.nom}`}
            </span>
            <EtageStatutBadge statut={etage.statut} className="ml-2" />
            <span className="ml-auto text-xs text-blue-700 whitespace-nowrap">
              {etage.salles.length} salle(s)
              {etage.surface_totale ? ` · ${etage.surface_totale} m²` : ''}
            </span>
          </button>
        </td>
        <td className={`px-3 ${pad.etage} text-right`}>
          <button
            onClick={onEditEtage}
            className="p-1 rounded text-blue-700 hover:bg-blue-100"
            title="Modifier l'étage"
          >
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>

      {expanded &&
        (etage.salles.length === 0 ? (
          <tr>
            <td colSpan={8} className="px-3 py-2 pl-16 text-xs text-gray-500 italic">
              Aucune salle dans cet étage.
              <button
                onClick={onAddSalle}
                className="ml-2 text-primary-600 hover:underline inline-flex items-center"
              >
                <Plus className="w-3 h-3 mr-0.5" />
                Ajouter une salle
              </button>
            </td>
          </tr>
        ) : (
          <>
            {etage.salles.map((salle) => (
              <SalleRow
                key={salle.id}
                salle={salle}
                selected={selectedSalles.has(salle.id)}
                pad={pad}
                inlineEdit={inlineEdit}
                savingInline={savingInline}
                onToggleSelection={() => onToggleSalleSelection(salle.id)}
                onEdit={() => onEditSalle(salle.id)}
                onDetail={() => onDetailSalle(salle.id)}
                onStartInlineEdit={onStartInlineEdit}
                onChangeInlineEdit={onChangeInlineEdit}
                onCommitInlineEdit={onCommitInlineEdit}
                onCancelInlineEdit={onCancelInlineEdit}
              />
            ))}
            <tr>
              <td colSpan={8} className="px-3 py-1 pl-16 bg-gray-50/30">
                <button
                  onClick={onAddSalle}
                  className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Ajouter une salle
                </button>
              </td>
            </tr>
          </>
        ))}
    </>
  );
}

function SalleRow({
  salle,
  selected,
  pad,
  inlineEdit,
  savingInline,
  onToggleSelection,
  onEdit,
  onDetail,
  onStartInlineEdit,
  onChangeInlineEdit,
  onCommitInlineEdit,
  onCancelInlineEdit,
}: {
  salle: SalleNode;
  selected: boolean;
  pad: { salle: string; etage: string; batiment: string };
  onToggleSelection(): void;
  onEdit(): void;
  onDetail(): void;
} & InlineEditProps) {
  const rowBg = selected ? 'bg-primary-50/40 hover:bg-primary-50' : 'hover:bg-gray-50';
  const isEditingNom = inlineEdit?.salleId === salle.id && inlineEdit.field === 'nom';
  const isEditingStatut = inlineEdit?.salleId === salle.id && inlineEdit.field === 'statut';

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      onCommitInlineEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancelInlineEdit();
    }
  }

  return (
    <tr className={`${rowBg} cursor-pointer`} onClick={onDetail}>
      <td className={`px-3 ${pad.salle} pl-16`} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelection}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          aria-label="Sélectionner cette salle"
        />
      </td>
      <td className={`px-3 ${pad.salle}`}>
        <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
          {salle.code}
        </span>
        {salle.numero_porte && (
          <span className="ml-2 text-xs text-gray-400">#{salle.numero_porte}</span>
        )}
      </td>

      {/* Cellule "Nom" — éditable inline au double-clic. */}
      <td
        className={`px-3 ${pad.salle} text-gray-900`}
        onClick={(e) => {
          if (isEditingNom) e.stopPropagation();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onStartInlineEdit(salle.id, 'nom', salle.nom);
        }}
        title={isEditingNom ? '' : 'Double-cliquez pour modifier'}
      >
        {isEditingNom ? (
          <input
            type="text"
            value={inlineEdit.value}
            onChange={(e) => onChangeInlineEdit(e.target.value)}
            onBlur={onCommitInlineEdit}
            onKeyDown={handleKey}
            disabled={savingInline}
            autoFocus
            className="w-full px-2 py-1 border border-primary-400 ring-2 ring-primary-200 rounded text-sm bg-white"
          />
        ) : (
          salle.nom
        )}
      </td>

      <td className={`hidden md:table-cell px-3 ${pad.salle} text-gray-600 capitalize`}>
        {salle.type}
      </td>
      <td className={`hidden lg:table-cell px-3 ${pad.salle} text-right text-gray-900`}>
        {salle.surface ? `${salle.surface} m²` : '—'}
      </td>

      {/* Cellule "Statut" — éditable inline via select. */}
      <td
        className={`px-3 ${pad.salle}`}
        onClick={(e) => {
          if (isEditingStatut) e.stopPropagation();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onStartInlineEdit(salle.id, 'statut', salle.statut);
        }}
        title={isEditingStatut ? '' : 'Double-cliquez pour modifier'}
      >
        {isEditingStatut ? (
          <select
            value={inlineEdit.value}
            onChange={(e) => onChangeInlineEdit(e.target.value)}
            onBlur={onCommitInlineEdit}
            onKeyDown={handleKey}
            disabled={savingInline}
            autoFocus
            className="px-2 py-1 border border-primary-400 ring-2 ring-primary-200 rounded text-xs bg-white"
          >
            <option value="disponible">Disponible</option>
            <option value="occupee">Occupée</option>
            <option value="en_maintenance">En maintenance</option>
            <option value="en_nettoyage">En nettoyage</option>
            <option value="hors_service">Hors service</option>
            <option value="reservee">Réservée</option>
            <option value="en_travaux">En travaux</option>
          </select>
        ) : (
          <SalleStatutBadge statut={salle.statut} />
        )}
      </td>

      <td className={`hidden xl:table-cell px-3 ${pad.salle} text-gray-600`}>
        {salle.service?.nom_complet ?? '—'}
      </td>
      <td className={`px-3 ${pad.salle} text-right`} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onEdit}
          className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
          aria-label="Modifier"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Badges & KPI
// ─────────────────────────────────────────────────────────────────────────────

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone: 'default' | 'primary' | 'green' | 'yellow' | 'gray';
}) {
  const tones: Record<typeof tone, string> = {
    default: 'bg-white text-gray-900',
    primary: 'bg-primary-50 text-primary-700',
    green: 'bg-green-50 text-green-700',
    yellow: 'bg-yellow-50 text-yellow-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return (
    <div className={`rounded-xl border border-gray-200 p-4 shadow-sm ${tones[tone]}`}>
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function BatimentStatutBadge({
  statut,
  className,
}: {
  statut: BatimentStatut;
  className?: string;
}) {
  const cfg: Record<BatimentStatut, { cls: string; label: string; Icon: typeof CheckCircle }> = {
    en_service: {
      cls: 'bg-green-100 text-green-800 border-green-200',
      label: 'En service',
      Icon: CheckCircle,
    },
    en_travaux: {
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      label: 'En travaux',
      Icon: Hammer,
    },
    ferme_temporairement: {
      cls: 'bg-gray-200 text-gray-800 border-gray-300',
      label: 'Fermé',
      Icon: XCircle,
    },
    en_projet: {
      cls: 'bg-blue-100 text-blue-800 border-blue-200',
      label: 'En projet',
      Icon: Building2,
    },
    desaffecte: {
      cls: 'bg-red-100 text-red-800 border-red-200',
      label: 'Désaffecté',
      Icon: XCircle,
    },
  };
  const c = cfg[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${c.cls} ${className ?? ''}`}
    >
      <c.Icon className="w-3 h-3 mr-1" />
      {c.label}
    </span>
  );
}

function EtageStatutBadge({ statut, className }: { statut: EtageStatut; className?: string }) {
  if (statut === 'operationnel') return null; // pas de badge pour le défaut
  const cfg: Record<EtageStatut, { cls: string; label: string }> = {
    operationnel: { cls: '', label: '' },
    acces_restreint: {
      cls: 'bg-orange-100 text-orange-800 border-orange-200',
      label: 'Accès restreint',
    },
    en_renovation: {
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      label: 'En rénovation',
    },
    ferme: { cls: 'bg-gray-200 text-gray-800 border-gray-300', label: 'Fermé' },
    acces_interdit: { cls: 'bg-red-100 text-red-800 border-red-200', label: 'Accès interdit' },
  };
  const c = cfg[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${c.cls} ${className ?? ''}`}
    >
      {c.label}
    </span>
  );
}

function SalleStatutBadge({ statut }: { statut: SalleStatut }) {
  const cfg: Record<SalleStatut, { cls: string; label: string; Icon: typeof CheckCircle }> = {
    disponible: {
      cls: 'bg-green-100 text-green-800 border-green-200',
      label: 'Disponible',
      Icon: CheckCircle,
    },
    occupee: {
      cls: 'bg-blue-100 text-blue-800 border-blue-200',
      label: 'Occupée',
      Icon: CheckCircle,
    },
    en_maintenance: {
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      label: 'Maintenance',
      Icon: Wrench,
    },
    en_nettoyage: {
      cls: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      label: 'Nettoyage',
      Icon: Wrench,
    },
    hors_service: {
      cls: 'bg-red-100 text-red-800 border-red-200',
      label: 'Hors service',
      Icon: XCircle,
    },
    reservee: {
      cls: 'bg-purple-100 text-purple-800 border-purple-200',
      label: 'Réservée',
      Icon: Calendar,
    },
    en_travaux: {
      cls: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      label: 'En travaux',
      Icon: Hammer,
    },
  };
  const c = cfg[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${c.cls}`}
    >
      <c.Icon className="w-3 h-3 mr-1" />
      {c.label}
    </span>
  );
}

void ChevronLeft;
void ChevronRight;
